'use-strict';

const { sum } = require('@hints/utils/math');
const Protocol = require('./protocol');
const config = require('../data/physical-config.json');
const bodyAreaConfig = require('../data/body-area-config.json');
const { clamp0100, weightedAverage, normalize0100, normalizeInverse0100 } = require('../utils/math');
const { isset, isEmpty } = require('@hints/utils/data');

/**
 * @typedef {Object} PainPoint
 * @property {number} x
 * @property {number} y
 * @property {'low' | 'medium' | 'high'} level
 * @property {'small' | 'medium' | 'large'} size
 */

/**
 * @typedef {Object} PhysicalScoreParams
 * 
 * PS PROPERTIES
 * @property {string[]} practicedSport
 * @property {string[]} practicedSubSport
 * @property {string} practiceFrequency
 * @property {string} practiceDuration
 * @property {string} practiceLoad
 * 
 * EVA PROPERTIES
 * @property {number} wellness
 * @property {number} recovery
 * @property {number} fatigue
 * 
 * PAIN PROPERTIES
 * @property {PainPoint[]} backPainPoints
 * @property {PainPoint[]} frontPainPoints
 * 
 * FACTOR PROPERTIES
 * @property {string[]} fatigueAndPainFactors
 */

class PhysicalProtocol extends Protocol {
    constructor() {
        super();
        this.sports = Object.keys(config.sports);
        this.subSports = Object.values(config.sports).flat();
        this.subSportsPerSport = config.sports;
        this.practiceLoads = config.practiceLoads;
        this.practiceDurations = config.practiceDurations;
        this.practiceFrequencies = config.practiceFrequencies;
        this.painLevels = Object.keys(config.painLevelValues);
        this.painSizes = Object.keys(config.painSizeValues);
        this.painAreas = config.painAreas;
        this.fatigueAndPainFactors = config.fatigueAndPainFactors;
        this.backRects = bodyAreaConfig.backRects;
        this.frontRects = bodyAreaConfig.frontRects;
    }

    /**
     * @param {'ms' | 'mr'} gender 
     * @param {number} age 
     * @param {PhysicalScoreParams} data 
     */
    getScore(_evaluation, _gender, _age, data) {
        const wellnessScore = normalize0100(data.wellness || 0, 0, 10);
        const recoveryScore = normalize0100(data.recovery || 0, 0, 10);
        const fatigueScore = normalizeInverse0100(data.fatigue || 0, 0, 10);
        const painScore = this.getPainScore(data);
        const score = weightedAverage(
            [wellnessScore, recoveryScore, fatigueScore, 100 - painScore],
            [config.wellnessCoeff, config.recoveryCoeff, config.fatigueCoeff, config.painCoeff]
        );
        return {
            data: {
                score: clamp0100(score),
                painScore: clamp0100(painScore),
                wellnessScore: clamp0100(wellnessScore),
                recoveryScore: clamp0100(recoveryScore),
                fatigueScore: clamp0100(fatigueScore),
                result: {
                    wellness: data.wellness,
                    recovery: data.recovery,
                    fatigue: data.fatigue,
                    backPainPoints: data.backPainPoints,
                    frontPainPoints: data.frontPainPoints,
                    practicedSport: data.practicedSport,
                    practicedSubSport: data.practicedSubSport,
                    practiceFrequency: data.practiceFrequency,
                    practiceDuration: data.practiceDuration,
                    practiceLoad: data.practiceLoad,
                    fatigueAndPainFactors: data.fatigueAndPainFactors
                }
            },
        };
    }

    validateResults(body) {
        if (!super.validateResults(body)) return false;
        const { painScore, wellnessScore, recoveryScore, fatigueScore, result } = body;
        if ([painScore, wellnessScore, recoveryScore, fatigueScore, result].some(v => !isset(v))) return false;
        const { wellness, recovery, fatigue, backPainPoints, frontPainPoints, fatigueAndPainFactors } = result;
        if ([wellness, recovery, fatigue, backPainPoints, frontPainPoints].some(v => !isset(v))) return false;
        if (!this.validateFactors(fatigueAndPainFactors, this.fatigueAndPainFactors)) return false;
        return true;
    }

    /**
     * @param {PhysicalScoreParams} data 
     */
    getPainScore(data) {
        const totalFront = sum(data.frontPainPoints.map(p => this.getPainPointValue(p)));
        const totalBack = sum(data.backPainPoints.map(p => this.getPainPointValue(p)));
        return clamp0100(totalFront + totalBack);
    }

    /**
     * 
     * @param {PainPoint} point 
     */
    getPainPointValue(point) {
        const { level = 'low', size = 'small' } = point;
        return (config.painLevelValues[level] * config.painSizeValues[size] * 3);
    }

    getPainArea(side, x, y) {
        const rects = this.getPainAreaRects(side);
        if (isEmpty(rects)) return undefined;
        const rect = rects.find(rect => {
            if (x < rect.x || x > rect.x + rect.w) return false;
            if (y < rect.y || y > rect.y + rect.h) return false;
            return true;
        });
        if (rect) return rect.area;
        return undefined;
    }

    getPainAreaRects(side) {
        switch (side) {
            case 'front': return this.frontRects;
            case 'back': return this.backRects;
            default: return [];
        }
    }
}

module.exports = new PhysicalProtocol();